/**
 * @fileoverview NoticeSettings - Configuration for automated notice parameters
 * Uses view/edit mode pattern with SettingsViewEdit component
 * Manages thresholds for "No Checkout for Period" notices per institution category
 */
import React, { useCallback } from 'react';
import {
    useGet,
    intl,
    fetchData,
    HTTP_STATUS
} from 'dmsbasereact';
import { Box, Typography, Grid, Chip } from '@mui/material';
import { SettingsViewEdit } from '../../components/SettingsLayouts';
import Swal from 'sweetalert2';

const inputStyle = {
    width: '100%',
    padding: '8px',
    borderRadius: '4px',
    border: '1px solid #ccc',
    fontSize: '14px'
};

/**
 * Renders a category section (Distributor, Dispenser, or Intermediary) in view mode
 */
const CategoryViewSection = ({ title, checkinCount, days }) => {
    const isDisabled = checkinCount === 0;

    return (
        <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    {title}
                </Typography>
                <Chip
                    label={isDisabled
                        ? intl.get('NoticeSettings.Disabled').d('Disabled')
                        : intl.get('NoticeSettings.Enabled').d('Enabled')
                    }
                    size="small"
                    color={isDisabled ? 'default' : 'success'}
                    variant="outlined"
                />
            </Box>
            <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={4}>
                    <Typography variant="caption" color="text.secondary">
                        {intl.get('NoticeSettings.CheckinCount').d('Minimum checkins')}
                    </Typography>
                    <Typography variant="body1">{checkinCount}</Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                    <Typography variant="caption" color="text.secondary">
                        {intl.get('NoticeSettings.Days').d('Period in days')}
                    </Typography>
                    <Typography variant="body1">{days}</Typography>
                </Grid>
            </Grid>
        </Box>
    );
};

/**
 * View mode content - displays all notice settings as read-only
 */
const ViewContent = ({ data }) => {
    return (
        <Box>
            <CategoryViewSection
                title={intl.get('NoticeSettings.Distributor').d('Distributor')}
                checkinCount={data.no_checkout_checkin_count_distributor ?? 0}
                days={data.no_checkout_days_distributor ?? 0}
            />
            <CategoryViewSection
                title={intl.get('NoticeSettings.Dispenser').d('Dispenser')}
                checkinCount={data.no_checkout_checkin_count_dispenser ?? 0}
                days={data.no_checkout_days_dispenser ?? 0}
            />
            <CategoryViewSection
                title={intl.get('NoticeSettings.Intermediary').d('Intermediary')}
                checkinCount={data.no_checkout_checkin_count_intermediary ?? 0}
                days={data.no_checkout_days_intermediary ?? 0}
            />
        </Box>
    );
};

/**
 * Renders a category section in edit mode with numeric inputs
 */
const CategoryEditSection = ({ title, description, countField, daysField, formData, onFieldChange }) => {
    return (
        <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
                {title}
            </Typography>
            {description && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {description}
                </Typography>
            )}
            <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={4}>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                        {intl.get('NoticeSettings.CheckinCount').d('Minimum checkins')}
                    </Typography>
                    <input
                        type="number"
                        min="0"
                        value={formData[countField] ?? 0}
                        onChange={(e) => onFieldChange(countField, Math.max(0, parseInt(e.target.value, 10) || 0))}
                        style={inputStyle}
                    />
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                        {intl.get('NoticeSettings.CheckinCountHelp').d('Number of checkins to trigger a notice (0 = disabled)')}
                    </Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                        {intl.get('NoticeSettings.Days').d('Period in days')}
                    </Typography>
                    <input
                        type="number"
                        min="0"
                        value={formData[daysField] ?? 0}
                        onChange={(e) => onFieldChange(daysField, Math.max(0, parseInt(e.target.value, 10) || 0))}
                        style={inputStyle}
                    />
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                        {intl.get('NoticeSettings.DaysHelp').d('Lookback period in days to check for checkouts (0 = disabled)')}
                    </Typography>
                </Grid>
            </Grid>
        </Box>
    );
};

/**
 * Edit mode content - displays all notice settings as form fields
 */
const EditContent = ({ data, formData, onFieldChange }) => {
    return (
        <Box>
            <CategoryEditSection
                title={intl.get('NoticeSettings.Distributor').d('Distributor')}
                description={intl.get('NoticeSettings.DistributorDescription').d(
                    'Generate a notice when a distributor receives stock (checkins) without sending stock out (checkouts) in the period.'
                )}
                countField="no_checkout_checkin_count_distributor"
                daysField="no_checkout_days_distributor"
                formData={formData}
                onFieldChange={onFieldChange}
            />
            <CategoryEditSection
                title={intl.get('NoticeSettings.Dispenser').d('Dispenser')}
                description={intl.get('NoticeSettings.DispenserDescription').d(
                    'Generate a notice when a dispenser receives stock (checkins) without sending stock out (checkouts) in the period.'
                )}
                countField="no_checkout_checkin_count_dispenser"
                daysField="no_checkout_days_dispenser"
                formData={formData}
                onFieldChange={onFieldChange}
            />
            <CategoryEditSection
                title={intl.get('NoticeSettings.Intermediary').d('Intermediary')}
                description={intl.get('NoticeSettings.IntermediaryDescription').d(
                    'Generate a notice when an intermediary receives stock (checkins) without sending stock out (checkouts) in the period.'
                )}
                countField="no_checkout_checkin_count_intermediary"
                daysField="no_checkout_days_intermediary"
                formData={formData}
                onFieldChange={onFieldChange}
            />
        </Box>
    );
};

/**
 * NoticeSettings - Main component
 * Manages automated notice parameters using view/edit mode pattern
 */
export const NoticeSettings = () => {
    const { data, isLoading, refetch } = useGet('/notice_settings', null, true);

    const prepareFormData = useCallback((data) => {
        return {
            no_checkout_checkin_count_distributor: data.no_checkout_checkin_count_distributor ?? 0,
            no_checkout_days_distributor: data.no_checkout_days_distributor ?? 0,
            no_checkout_checkin_count_dispenser: data.no_checkout_checkin_count_dispenser ?? 0,
            no_checkout_days_dispenser: data.no_checkout_days_dispenser ?? 0,
            no_checkout_checkin_count_intermediary: data.no_checkout_checkin_count_intermediary ?? 0,
            no_checkout_days_intermediary: data.no_checkout_days_intermediary ?? 0,
        };
    }, []);

    const buildPayload = useCallback((formData) => {
        return {
            no_checkout_checkin_count_distributor: formData.no_checkout_checkin_count_distributor,
            no_checkout_days_distributor: formData.no_checkout_days_distributor,
            no_checkout_checkin_count_dispenser: formData.no_checkout_checkin_count_dispenser,
            no_checkout_days_dispenser: formData.no_checkout_days_dispenser,
            no_checkout_checkin_count_intermediary: formData.no_checkout_checkin_count_intermediary,
            no_checkout_days_intermediary: formData.no_checkout_days_intermediary,
        };
    }, []);

    const handleSave = useCallback(async (payload) => {
        try {
            const response = await fetchData('/notice_settings', 'PUT', true, payload, false, true);

            if (response.status === HTTP_STATUS.OK_200) {
                await Swal.fire({
                    title: intl.get('Success').d('Success'),
                    text: intl.get('SettingsSaved').d('Settings saved successfully'),
                    icon: 'success',
                    confirmButtonText: intl.get('OK').d('OK')
                });
                return response.data;
            } else {
                await Swal.fire({
                    title: intl.get('Error').d('Error'),
                    text: response.message || intl.get('SaveError').d('Failed to save settings'),
                    icon: 'error',
                    confirmButtonText: intl.get('OK').d('OK')
                });
                return null;
            }
        } catch (error) {
            await Swal.fire({
                title: intl.get('Error').d('Error'),
                text: error.message || intl.get('SaveError').d('Failed to save settings'),
                icon: 'error',
                confirmButtonText: intl.get('OK').d('OK')
            });
            return null;
        }
    }, []);

    return (
        <Box>
            <SettingsViewEdit
                data={data?.data}
                loading={isLoading}
                title={intl.get('NoticeSettings.Title').d('No Checkout for Period')}
                description={intl.get('NoticeSettings.Description').d(
                    'Configure thresholds for detecting institutions that receive stock (checkins) without sending stock out (checkouts). Set the minimum number of checkins and the lookback period in days for each institution category. Set values to 0 to disable detection for a category.'
                )}
                viewContent={ViewContent}
                editContent={EditContent}
                prepareFormData={prepareFormData}
                buildPayload={buildPayload}
                onSave={handleSave}
                onDataUpdated={refetch}
                confirmTitle={intl.get('NoticeSettings.ConfirmTitle').d('Confirm Save Notice Settings')}
                confirmMessage={intl.get('NoticeSettings.ConfirmMessage').d(
                    'Are you sure you want to save these notice settings? Changes will affect the automated notice detection cycle.'
                )}
            />
        </Box>
    );
};
